import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Pagination,
  CircularProgress,
  Grid,
  Divider,
  TextField,
  Tooltip,
} from '@mui/material';
import { Visibility, CheckCircle, Cancel, Store, AccessTime } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import {
  fetchAdminProducts,
  updateApprovalStatus,
} from '../../../State/admin/adminProductSlice';
import { Product, ProductStatus } from '../../../types/productTypes';

const AdminProductTable = () => {
  const dispatch = useAppDispatch();

  const {
    pendingProducts = [],
    approvedProducts = [],
    rejectedProducts = [],
    totalPendingPages = 0,
    totalApprovedPages = 0,
    totalRejectedPages = 0,
    loading = false,
  } = useAppSelector((state) => state.adminProduct || {});

  const [activeTab, setActiveTab] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  // Preview Modal
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');

  // Rejection Dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState<boolean>(false);
  const [rejectingProductId, setRejectingProductId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState<string>('');

  const mapTabToStatus = (tab: number): ProductStatus => {
    switch (tab) {
      case 1:
        return 'APPROVED';
      case 2:
        return 'REJECTED';
      case 0:
      default:
        return 'PENDING';
    }
  };

  useEffect(() => {
    const status = mapTabToStatus(activeTab);
    dispatch(fetchAdminProducts({ status, page: page - 1 }));
  }, [dispatch, activeTab, page]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleApprove = (productId: number) => {
    dispatch(updateApprovalStatus({ productId, status: 'APPROVED' }));
    if (previewProduct?.id === productId) setPreviewProduct(null);
  };

  const handleOpenRejectDialog = (productId: number) => {
    setRejectingProductId(productId);
    setRejectComment('');
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = () => {
    if (rejectingProductId !== null) {
      dispatch(
        updateApprovalStatus({
          productId: rejectingProductId,
          status: 'REJECTED',
          comment: rejectComment.trim() || null,
        })
      );
      if (previewProduct?.id === rejectingProductId) setPreviewProduct(null);
    }
    setRejectDialogOpen(false);
    setRejectingProductId(null);
    setRejectComment('');
  };

  const handleOpenPreview = (product: Product) => {
    setPreviewProduct(product);
    setSelectedImage(product.images?.[0] || '');
  };

  const getCurrentProducts = (): Product[] => {
    if (activeTab === 0) return pendingProducts;
    if (activeTab === 1) return approvedProducts;
    return rejectedProducts;
  };

  const getTotalPages = (): number => {
    if (activeTab === 0) return totalPendingPages;
    if (activeTab === 1) return totalApprovedPages;
    return totalRejectedPages;
  };

  const currentProducts = getCurrentProducts();
  const totalPages = getTotalPages();

  const formatDate = (dateString?: Date | string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusChip = (product: Product) => {
    const status = product.status || (product.isApproved ? 'APPROVED' : 'PENDING');
    switch (status) {
      case 'APPROVED':
        return <Chip label="Approved" color="success" size="small" />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" />;
      case 'PENDING':
      default:
        return <Chip label="Pending" color="warning" size="small" />;
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Product Approvals Management
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Sorted by: <strong>Recently Added</strong>
        </Typography>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label={`Pending (${pendingProducts?.length || 0})`} />
          <Tab label={`Approved (${approvedProducts?.length || 0})`} />
          <Tab label={`Rejected (${rejectedProducts?.length || 0})`} />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ backgroundColor: '#1e293b' }}>
            <TableRow>
              <TableCell sx={{ color: 'white' }}>Image</TableCell>
              <TableCell sx={{ color: 'white' }}>Title</TableCell>
              <TableCell sx={{ color: 'white' }}>Date Added</TableCell>
              <TableCell sx={{ color: 'white' }}>Seller</TableCell>
              <TableCell sx={{ color: 'white' }} align="right">Selling Price</TableCell>
              <TableCell sx={{ color: 'white' }} align="center">Status</TableCell>
              <TableCell sx={{ color: 'white' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : currentProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No products found in this status.
                </TableCell>
              </TableRow>
            ) : (
              currentProducts.map((product: Product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <img
                      src={product.images?.[0] || '/placeholder.png'}
                      alt={product.title}
                      style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{product.title}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Creation Time">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTime fontSize="inherit" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(product.createdAt)}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Store fontSize="small" color="action" />
                      <Typography variant="body2">{product.seller?.sellerName || 'N/A'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    KES {product.sellingPrice?.toLocaleString()}
                  </TableCell>
                  <TableCell align="center">{getStatusChip(product)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="info" size="small" onClick={() => handleOpenPreview(product)}>
                        <Visibility />
                      </IconButton>

                      {/* Hide Approve button if status is APPROVED or REJECTED */}
                      {product.status !== 'APPROVED' && product.status !== 'REJECTED' && (
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleApprove(product.id!)}
                        >
                          Approve
                        </Button>
                      )}

                      {product.status !== 'REJECTED' && (
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleOpenRejectDialog(product.id!)}
                        >
                          Reject
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      )}

      {/* Reject Modal */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Reject Product</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally specify why this product is being rejected.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Comment (Optional)"
            placeholder="e.g., Image quality is low, or price is invalid"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmReject} variant="contained" color="error">
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Preview Modal */}
      <Dialog open={!!previewProduct} onClose={() => setPreviewProduct(null)} maxWidth="md" fullWidth>
        {previewProduct && (
          <>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Product Details</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box
                    component="img"
                    src={selectedImage || previewProduct.images?.[0]}
                    alt="Main Preview"
                    sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, mb: 1.5, border: '1px solid #e2e8f0' }}
                  />
                  <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                    {previewProduct.images?.map((img: string, idx: number) => (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        onClick={() => setSelectedImage(img)}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: selectedImage === img ? '2px solid #1976d2' : '1px solid #ccc',
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {previewProduct.title}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Submitted: {formatDate(previewProduct.createdAt)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {previewProduct.description}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">MRP Price</Typography>
                      <Typography variant="body1" fontWeight={600}>KES {previewProduct.mrpPrice}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Selling Price</Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">KES {previewProduct.sellingPrice}</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8fafc' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      Seller Info
                    </Typography>
                    <Typography variant="body2"><strong>Name:</strong> {previewProduct.seller?.sellerName || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {previewProduct.seller?.email || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Mobile:</strong> {previewProduct.seller?.mobile || 'N/A'}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setPreviewProduct(null)} color="inherit">
                Close
              </Button>
              {/* Hide Approve button if status is APPROVED or REJECTED */}
              {previewProduct.status !== 'APPROVED' && previewProduct.status !== 'REJECTED' && (
                <Button variant="contained" color="success" onClick={() => handleApprove(previewProduct.id!)}>
                  Approve Product
                </Button>
              )}
              {previewProduct.status !== 'REJECTED' && (
                <Button variant="contained" color="error" onClick={() => handleOpenRejectDialog(previewProduct.id!)}>
                  Reject Product
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminProductTable;